import React, { useRef } from 'react';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { shortenandHoverString } from '../../../../Components/helper';
import { ExpandChevronCell } from '../../../../Components/BombiixAGGrid';
import type { BombiixCellRendererParams, BombiixHeaderParams } from '../../../../Components/BombiixAGGrid';
import type { CostingGridRow, CostingRecord } from './store/Costing.type';

/* Cell and header renderers private to the Costing TODO table. Co-located in one module
   following the DesignDetailsBulkGrid/bulk-grid-cells.tsx precedent. `ImageCell` and
   `ExpandAllHeader` are prefixed here because Components/BombiixAGGrid already exports
   components under those names — the Bombiix ExpandAllHeader is AG-Grid-node driven and is
   not a drop-in for this caller-controlled one. */

type CostingCellParams = BombiixCellRendererParams<CostingGridRow, React.ReactNode>;

export interface CostingHeaderExtras {
  label?: React.ReactNode;
  sortKey?: string;
  onSort?: (sortKey: string) => void;
  sortOrder?: string;
  sortPropertyName?: string;
}

/** Read-only cell whose content is produced by a caller-supplied accessor. */
export function NodeCell(params: CostingCellParams & { accessor?: (row: CostingGridRow) => React.ReactNode }) {
  const { data, accessor } = params;
  const content = typeof accessor === 'function' ? (data ? accessor(data) : '') : params.value;
  const isEmptyContent = content === undefined || content === null || content === '';
  return (
    <div className="cag-cell cag-cell-readonly">
      <span>{isEmptyContent ? '' : content}</span>
    </div>
  );
}

/**
 * Header with server-side sort affordance. Sorting is driven by the label click rather than AG Grid,
 * because the list is sorted on the server.
 *
 * The aggregate chips are NOT rendered here — they sit in a band above the grid (see
 * BombiixAGGrid's `headerStats`), because AG Grid clips anything painted above its own root.
 */
export function CostingHeader(params: BombiixHeaderParams<CostingGridRow> & CostingHeaderExtras) {
  const { label, sortKey, onSort, sortOrder, sortPropertyName, showColumnMenu, column } = params;
  let sortIcon = null;
  if (sortKey) {
    if (sortPropertyName !== sortKey || isEmpty(sortOrder)) sortIcon = 'fa-sort';
    else sortIcon = sortOrder === 'Asc' ? 'fa-sort-up' : 'fa-sort-desc';
  }
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const showMenu = typeof showColumnMenu === 'function' && !column?.getColDef?.()?.suppressHeaderMenuButton;

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (menuBtnRef.current) showColumnMenu(menuBtnRef.current);
  };

  return (
    <div className="cag-cell d-flex align-items-center">
      <span className={sortKey ? 'cursor-pointer' : ''} onClick={sortKey && onSort ? () => onSort(sortKey) : undefined}>
        {label}
        {sortIcon && <i className={`fa fa ${sortIcon} ml-1`} />}
      </span>
      {showMenu && (
        <button ref={menuBtnRef} type="button" className="cag-header-menu-btn" onClick={handleMenuClick} aria-label="Column menu" title="Column menu">
          <i className="icon-More" />
        </button>
      )}
    </div>
  );
}

/**
 * Expand/collapse-all toggle in the hierarchy column's header. Uses the shared BombiixAGGrid
 * `ExpandChevronCell` in controlled mode so the affordance matches the BulkEdit Measurements grid
 * (and every other chevron in the app) rather than a bespoke icon pair.
 */
export function CostingExpandAllHeader(params: BombiixHeaderParams<CostingGridRow> & { allExpanded?: boolean; onToggleAll?: (next: boolean) => void }) {
  const { allExpanded, onToggleAll } = params;
  return (
    <ExpandChevronCell
      expanded={Boolean(allExpanded)}
      onToggle={() => onToggleAll?.(!allExpanded)}
      expandLabel="Expand all costings"
      collapseLabel="Collapse all costings"
      className="cag-chevron-toggle"
    />
  );
}

/**
 * Per-row expand chevron, shared-component variant of the same affordance. Controlled mode: the
 * combinations expand by the table's own expandedRowIndex state, not AG Grid node expansion.
 * Space-preserving blank on rows with nothing to expand, so the column stays aligned.
 */
export function CostingExpandCell(params: CostingCellParams & { getExpanded?: (row?: CostingGridRow) => boolean; onToggle?: (row?: CostingGridRow) => void }) {
  const { data, getExpanded, onToggle } = params;
  if (!(data?.__rowType === 'parent' && data.__showChevron)) return <span className="cag-chevron cag-chevron--hidden" aria-hidden="true" />;
  return (
    <ExpandChevronCell
      expanded={Boolean(getExpanded?.(data))}
      onToggle={() => onToggle?.(data)}
      expandLabel="Expand combinations"
      collapseLabel="Collapse combinations"
      className="cag-chevron-toggle"
    />
  );
}

/** Product number, plain — the brand/season summary popover was removed by request (2026-08-04). */
export function NumberCell(params: CostingCellParams) {
  const { data } = params;
  if (data?.__rowType !== 'parent') return <div className="cag-cell" />;
  return <div className="cag-cell d-flex align-items-center">{shortenandHoverString(data.cost?.number || '--', 20)}</div>;
}

/**
 * Costing reference rendered as an underlined link that opens the same edit flow as the actions
 * column's former edit button — which this replaces. Without edit permission the reference keeps
 * the link styling but is not clickable, so the column reads identically for every user.
 */
export function CostingRefLinkCell(params: CostingCellParams & { onEdit?: (cost: CostingRecord) => void; canEdit?: boolean }) {
  const { data, onEdit, canEdit } = params;
  if (data?.__rowType !== 'parent') return <div className="cag-cell" />;
  const { cost } = data;
  const reference = cost?.productInfo?.reference || cost?.reference || '--';
  return (
    <div className="cag-cell">
      {canEdit && cost ? (
        <button type="button" className="costing-ref-link" onClick={() => onEdit?.(cost)}>
          {shortenandHoverString(reference, 24)}
        </button>
      ) : (
        <span className="costing-ref-link costing-ref-link--static">{shortenandHoverString(reference, 24)}</span>
      )}
    </div>
  );
}

/** Most recent comment, truncated, blank on expanded child rows. */
export function CommentCell(params: CostingCellParams) {
  const { data } = params;
  if (data?.__rowType !== 'parent') return <div className="cag-cell" />;
  return <div className="cag-cell">{shortenandHoverString(data.cost?.comments?.message || '--', 50)}</div>;
}

/* Status pill background per status. Partially Complete shares the Pending amber by explicit
   decision; anything unrecognised falls back to the pill's neutral base. */
const STATUS_PILL_VARIANTS: Record<string, string> = {
  draft: 'costing-status-pill--draft',
  pending: 'costing-status-pill--pending',
  'partially complete': 'costing-status-pill--pending',
  complete: 'costing-status-pill--complete',
};

/** Status rendered as a colour-coded pill (replaces the former text + dot, 2026-08-04). */
export function StatusCell(params: CostingCellParams) {
  const { data } = params;
  if (data?.__rowType !== 'parent') return <div className="cag-cell" />;
  const status = data.cost?.status || '--';
  const variant = STATUS_PILL_VARIANTS[status.toLowerCase()] ?? '';
  return (
    <div className="cag-cell">
      <span className={`costing-status-pill${variant ? ` ${variant}` : ''}`} title={status}>
        {status}
      </span>
    </div>
  );
}

/**
 * Row actions: the created/modified hover popover only. The edit button moved to the Costing Ref
 * column, whose underlined reference now opens the same edit flow (2026-08-04).
 */
export function CostingActionsCell(params: CostingCellParams & { t?: TranslateFn }) {
  const { data, t } = params;
  if (data?.__rowType !== 'parent') return <div className="cag-cell" />;
  const { cost } = data;
  return (
    <div className="table-action-container">
      <button aria-label="info" type="button" className="bombiix-icon-container bombiix-icon-container--blue">
        <OverlayTrigger
          placement="bottom"
          trigger="hover"
          rootClose
          overlay={
            <Popover id={`costing-todo-modified-${data.__cagId}`} className="custom-popover modified-popover">
              <Popover.Content>
                <div className="information">
                  {cost?.createdBy && (
                    <p className="text-left m-0">
                      <strong>{t?.('DEVLOPMENT_ORDER.TESTING.PRODUCT_TESTING.CREATED')}</strong>: {`${cost?.createdBy} | `}
                      {moment(cost?.createdAt).format('DD-MMM-YYYY, h:mm a')}
                    </p>
                  )}
                  {cost?.modifiedBy && (
                    <p className="text-left m-0">
                      <strong>{t?.('DEVLOPMENT_ORDER.TESTING.PRODUCT_TESTING.MODIFIED')}</strong>: {`${cost?.modifiedBy}| `}
                      {moment(cost?.updatedAt).format('DD-MMM-YYYY, h:mm a')}
                    </p>
                  )}
                </div>
              </Popover.Content>
            </Popover>
          }
        >
          <i className="bombiix-icon-information" />
        </OverlayTrigger>
      </button>
    </div>
  );
}
